/**
 * Subscription Service
 * Handles subscription plan management, pricing calculations, and plan operations
 * Integrates with existing TrialService and OrganizationContext
 */

import { 
  SUBSCRIPTION_PLANS, 
  getPlanById, 
  calculateAdditionalUserCost, 
  getRecommendedPlan,
  formatPrice,
  calculateYearlySavingsPercentage 
} from '../data/subscriptionPlans';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, query, where, Timestamp } from 'firebase/firestore';

const normalizeToDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return isNaN(converted.getTime()) ? null : converted;
  }

  const converted = new Date(value);
  return isNaN(converted.getTime()) ? null : converted;
};

const getBillingInterval = (orgData, startDate, endDate) => {
  // FIX #8: Improved billing interval detection with proper priority
  // Priority 1: Direct billingInterval field
  if (orgData?.billingInterval === 'year' || orgData?.billingInterval === 'month') {
    return orgData.billingInterval;
  }

  // Priority 2: Most recent paymentHistory entry
  if (Array.isArray(orgData?.paymentHistory) && orgData.paymentHistory.length > 0) {
    // Get most recent payment (last in array)
    const mostRecentPayment = orgData.paymentHistory[orgData.paymentHistory.length - 1];
    if (mostRecentPayment?.billingInterval === 'year' || mostRecentPayment?.billingInterval === 'month') {
      return mostRecentPayment.billingInterval;
      }
    }

  // Priority 3: Infer from date difference (only if dates are valid)
  if (startDate && endDate && startDate instanceof Date && endDate instanceof Date) {
    const diffDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    // Use 300 days as threshold (more accurate than 365/2)
    if (diffDays > 300) {
      return 'year';
    }
    // If difference is reasonable for monthly (20-40 days), return month
    if (diffDays >= 20 && diffDays <= 40) {
      return 'month';
    }
  }

  // Default to monthly
  return 'month';
};

const ensureWindow = (startDate, endDate, defaultDays) => {
  // FIX #8: Improved window validation with better date handling
  if (!startDate) {
    return null;
  }

  const start = new Date(startDate.getTime());
  
  // Validate start date
  if (isNaN(start.getTime())) {
    return null;
  }

  let end = endDate ? new Date(endDate.getTime()) : null;

  // Validate end date
  if (end && isNaN(end.getTime())) {
    end = null;
  }

  // If no end date or invalid, calculate from start + defaultDays
  if (!end || end <= start) {
    end = new Date(start.getTime());
    // Use proper month/year addition instead of just days
    if (defaultDays === 366 || defaultDays === 365) {
      // Yearly: add 1 year (handles leap years)
      end.setFullYear(end.getFullYear() + 1);
    } else if (defaultDays >= 28 && defaultDays <= 31) {
      // Monthly: add 1 month (handles variable month lengths)
      end.setMonth(end.getMonth() + 1);
    } else {
      // Fallback: add days
    end.setDate(end.getDate() + defaultDays);
    }
  }

  // Final validation
  if (end <= start || isNaN(end.getTime())) {
    return null;
  }

  return { startDate: start, endDate: end };
};

const getActiveBillingWindow = (orgData) => {
  // FIX #8: Improved billing window calculation with accurate date handling
  if (!orgData) {
    return null;
  }

  const subscriptionStatus = orgData.subscriptionStatus || 'trial';

  if (subscriptionStatus === 'trial') {
    const trialStart = normalizeToDate(orgData.trialStartDate) || normalizeToDate(orgData.createdAt);
    const trialEnd = normalizeToDate(orgData.trialEndDate);
    return ensureWindow(trialStart, trialEnd, 30);
  }

  // For active subscriptions, use actual billing dates when available
  const baseStart = normalizeToDate(orgData.lastPaymentDate) ||
    normalizeToDate(orgData.subscriptionStartDate) ||
    normalizeToDate(orgData.trialEndDate) ||
    normalizeToDate(orgData.createdAt);

  // FIX #8: Handle cancelled subscriptions - use subscriptionEndsAt as window end
  const subscriptionEndsAt = normalizeToDate(orgData.subscriptionEndsAt);
  const willNotRenew = orgData.willNotRenew === true;
  
  // For cancelled subscriptions, use subscriptionEndsAt as the window end
  let baseEnd = null;
  if (willNotRenew && subscriptionEndsAt) {
    baseEnd = subscriptionEndsAt;
  } else {
    baseEnd = normalizeToDate(orgData.nextBillingDate);
  }

  // Get billing interval to determine default period
  const inferredInterval = getBillingInterval(orgData, baseStart, baseEnd);
  
  // FIX #8: Use accurate default days based on interval
  // For yearly: use 365/366 based on leap year, but ensureWindow will handle month addition
  // For monthly: use 31 (max month length), but ensureWindow will handle month addition
  const defaultDays = inferredInterval === 'year' ? 366 : 31;

  let window = ensureWindow(baseStart, baseEnd, defaultDays);

  if (!window) {
    return null;
  }

  // FIX #8: For cancelled subscriptions, ensure window doesn't extend beyond subscriptionEndsAt
  if (willNotRenew && subscriptionEndsAt && subscriptionEndsAt < window.endDate) {
    window = ensureWindow(window.startDate, subscriptionEndsAt, defaultDays);
  } else if (!willNotRenew && subscriptionEndsAt && subscriptionEndsAt < window.endDate) {
    // Also handle case where subscriptionEndsAt exists but willNotRenew is false
    // This shouldn't happen normally, but handle it defensively
    window = ensureWindow(window.startDate, subscriptionEndsAt, defaultDays);
  }

  return window;
};

class SubscriptionService {
  constructor() {
    this.serviceName = 'SubscriptionService';
  }

  /**
   * Get all available subscription plans
   * @returns {Array} Array of subscription plans
   */
  getAllPlans() {
    return Object.values(SUBSCRIPTION_PLANS);
  }

  /**
   * Get plan by ID
   * @param {string} planId - Plan identifier
   * @returns {Object|null} Plan object or null if not found
   */
  getPlan(planId) {
    return getPlanById(planId);
  }

  /**
   * Get recommended plan based on current usage
   * @param {number} userCount - Current number of users
   * @param {number} orderCount - Current number of orders (optional)
   * @returns {string} Recommended plan ID
   */
  getRecommendedPlan(userCount, orderCount = 0) {
    return getRecommendedPlan(userCount);
  }

  /**
   * Calculate plan pricing with additional user costs
   * @param {string} planId - Plan identifier
   * @param {number} currentUsers - Current number of users
   * @param {string} billingInterval - 'month' or 'year'
   * @returns {Object} Pricing breakdown
   */
  calculatePlanPricing(planId, currentUsers, billingInterval = 'month') {
    const plan = this.getPlan(planId);
    if (!plan) {
      throw new Error('Plan not found');
    }

    const basePrice = billingInterval === 'year' ? plan.yearlyPrice : plan.monthlyPrice;
    const additionalUserCost = calculateAdditionalUserCost(currentUsers, plan.limits.maxUsers);
    const totalPrice = basePrice + additionalUserCost;

    return {
      planId,
      planName: plan.name,
      basePrice,
      additionalUserCost,
      totalPrice,
      billingInterval,
      currency: plan.currency,
      savings: billingInterval === 'year' ? plan.yearlySavings : 0,
      savingsPercentage: billingInterval === 'year' ? 
        calculateYearlySavingsPercentage(plan.monthlyPrice, plan.yearlyPrice) : 0,
      formattedPrice: formatPrice(totalPrice),
      formattedBasePrice: formatPrice(basePrice),
      formattedAdditionalCost: formatPrice(additionalUserCost),
      formattedSavings: formatPrice(plan.yearlySavings)
    };
  }

  /**
   * Compare plans side by side
   * @param {Array} planIds - Array of plan IDs to compare
   * @returns {Array} Comparison data
   */
  comparePlans(planIds = ['basic', 'premium', 'premiumPlus', 'enterprise']) {
    return planIds.map(planId => {
      const plan = this.getPlan(planId);
      if (!plan) return null;

      return {
        ...plan,
        monthlyPriceFormatted: formatPrice(plan.monthlyPrice),
        yearlyPriceFormatted: formatPrice(plan.yearlyPrice),
        monthlySavingsFormatted: `Save ₹${plan.yearlySavings}`,
        yearlySavingsFormatted: `Save ₹${plan.yearlySavings}`,
        usersDisplay: plan.limits.maxUsers === -1 ? 'Unlimited' : `${plan.limits.maxUsers} users`,
        ordersDisplay: plan.limits.maxOrders === -1 ? 'Unlimited' : `${plan.limits.maxOrders} orders`,
        backupDisplay: plan.limits.backupDays === -1 ? 'Forever' : `${plan.limits.backupDays} days`
      };
    }).filter(Boolean);
  }

  /**
   * Check if current usage exceeds plan limits
   * @param {string} planId - Plan identifier
   * @param {Object} usage - Current usage data
   * @returns {Object} Limit check results
   */
  checkPlanLimits(planId, usage) {
    const plan = this.getPlan(planId);
    if (!plan) {
      throw new Error('Plan not found');
    }

    const { users = 0, orders = 0 } = usage;
    const limits = plan.limits;

    return {
      planId,
      planName: plan.name,
      limits: {
        users: {
          current: users,
          limit: limits.maxUsers,
          exceeded: limits.maxUsers !== -1 && users > limits.maxUsers,
          remaining: limits.maxUsers === -1 ? -1 : Math.max(0, limits.maxUsers - users)
        },
        orders: {
          current: orders,
          limit: limits.maxOrders,
          exceeded: limits.maxOrders !== -1 && orders > limits.maxOrders,
          remaining: limits.maxOrders === -1 ? -1 : Math.max(0, limits.maxOrders - orders)
        }
      },
      hasExceededLimits: (limits.maxUsers !== -1 && users > limits.maxUsers) || 
                        (limits.maxOrders !== -1 && orders > limits.maxOrders)
    };
  }

  /**
   * Get upgrade recommendations based on current usage
   * @param {Object} usage - Current usage data
   * @returns {Array} Array of recommended upgrades
   */
  getUpgradeRecommendations(usage) {
    const { users = 0, orders = 0 } = usage;
    const recommendations = [];

    // Check each plan to see if it would be suitable
    Object.values(SUBSCRIPTION_PLANS).forEach(plan => {
      const limits = plan.limits;
      const wouldFit = (limits.maxUsers === -1 || users <= limits.maxUsers) && 
                      (limits.maxOrders === -1 || orders <= limits.maxOrders);
      
      if (wouldFit) {
        const additionalUserCost = calculateAdditionalUserCost(users, limits.maxUsers);
        recommendations.push({
          planId: plan.id,
          planName: plan.name,
          monthlyPrice: plan.monthlyPrice,
          yearlyPrice: plan.yearlyPrice,
          additionalUserCost,
          totalMonthlyCost: plan.monthlyPrice + additionalUserCost,
          totalYearlyCost: plan.yearlyPrice + additionalUserCost,
          reason: this.getUpgradeReason(plan, usage)
        });
      }
    });

    // Sort by monthly price
    return recommendations.sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  }

  /**
   * Get upgrade reason for a plan
   * @param {Object} plan - Plan object
   * @param {Object} usage - Current usage data
   * @returns {string} Reason for upgrade
   */
  getUpgradeReason(plan, usage) {
    const { users = 0, orders = 0 } = usage;
    const limits = plan.limits;

    if (limits.maxUsers !== -1 && users > limits.maxUsers) {
      return `Supports up to ${limits.maxUsers} users (you have ${users})`;
    }
    if (limits.maxOrders !== -1 && orders > limits.maxOrders) {
      return `Supports up to ${limits.maxOrders} orders (you have ${orders})`;
    }
    if (limits.maxUsers === -1 && limits.maxOrders === -1) {
      return 'Unlimited users and orders';
    }
    return `Better features and higher limits`;
  }

  /**
   * Calculate cost savings for yearly billing
   * @param {string} planId - Plan identifier
   * @returns {Object} Savings calculation
   */
  calculateYearlySavings(planId) {
    const plan = this.getPlan(planId);
    if (!plan) {
      throw new Error('Plan not found');
    }

    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlyPrice = plan.yearlyPrice;
    const savings = monthlyTotal - yearlyPrice;
    const savingsPercentage = calculateYearlySavingsPercentage(plan.monthlyPrice, plan.yearlyPrice);

    return {
      planId,
      planName: plan.name,
      monthlyTotal,
      yearlyPrice,
      savings,
      savingsPercentage,
      formattedSavings: formatPrice(savings),
      formattedMonthlyTotal: formatPrice(monthlyTotal),
      formattedYearlyPrice: formatPrice(yearlyPrice)
    };
  }

  /**
   * Get plan features for display
   * @param {string} planId - Plan identifier
   * @returns {Array} Array of features
   */
  getPlanFeatures(planId) {
    const plan = this.getPlan(planId);
    return plan ? plan.features : [];
  }

  /**
   * Validate plan selection
   * @param {string} planId - Plan identifier
   * @param {Object} usage - Current usage data
   * @returns {Object} Validation result
   */
  validatePlanSelection(planId, usage) {
    const plan = this.getPlan(planId);
    if (!plan) {
      return {
        valid: false,
        error: 'Plan not found'
      };
    }

    const limitCheck = this.checkPlanLimits(planId, usage);
    if (limitCheck.hasExceededLimits) {
      return {
        valid: false,
        error: 'Current usage exceeds plan limits',
        details: limitCheck
      };
    }

    return {
      valid: true,
      plan,
      limitCheck
    };
  }

  // ===== FILE RETENTION METHODS =====

  /**
   * Get backup period (in days) for a subscription plan
   * @param {string} planId - Plan identifier
   * @returns {number} Backup period in days (-1 for forever)
   */
  getBackupPeriod(planId) {
    const plan = this.getPlan(planId);
    if (!plan) {
      return 30; // Default to Basic plan
    }
    return plan.limits.backupDays;
  }

  /**
   * Check if files are within backup period based on upload date
   * @param {Date|Timestamp} uploadDate - When the file was uploaded
   * @param {string} planId - Subscription plan ID
   * @returns {boolean} True if file is within backup period
   */
  checkFileRetentionLimits(uploadDate, planId) {
    const backupDays = this.getBackupPeriod(planId);
    
    // Forever backup (Enterprise plan)
    if (backupDays === -1) {
      return true;
    }

    const uploadDateObj = uploadDate?.toDate ? uploadDate.toDate() : new Date(uploadDate);
    const now = new Date();
    
    // Set both dates to start of day for consistent calculation
    const uploadStartOfDay = new Date(uploadDateObj.getFullYear(), uploadDateObj.getMonth(), uploadDateObj.getDate());
    const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const daysSinceUpload = Math.floor((nowStartOfDay - uploadStartOfDay) / (1000 * 60 * 60 * 24));
    
    return daysSinceUpload < backupDays;
  }

  /**
   * Calculate backup expiry date for a file based on upload date and plan
   * @param {Date|Timestamp} uploadDate - When the file was uploaded
   * @param {string} planId - Subscription plan ID
   * @returns {Date|null} Expiry date (null for forever backup)
   */
  calculateBackupExpiryDate(uploadDate, planId) {
    const backupDays = this.getBackupPeriod(planId);
    
    // Forever backup (Enterprise plan)
    if (backupDays === -1) {
      return null;
    }

    // Convert to Date if it's a Firestore Timestamp
    const uploadDateObj = uploadDate?.toDate ? uploadDate.toDate() : new Date(uploadDate);
    
    // Calculate expiry date - set to end of day to ensure full day calculation
    const expiryDate = new Date(uploadDateObj);
    expiryDate.setDate(expiryDate.getDate() + backupDays);
    expiryDate.setHours(23, 59, 59, 999); // Set to end of day
    
    return expiryDate;
  }

  /**
   * Get cleanup recommendations based on file age and plan
   * @param {string} organizationId - Organization ID
   * @param {number} fileAge - Age of files in days
   * @returns {Array} Array of cleanup recommendations
   */
  getCleanupRecommendations(organizationId, fileAge) {
    const recommendations = [];
    
    // This would typically get the organization's current plan
    // For now, we'll provide generic recommendations
    if (fileAge > 30) {
      recommendations.push({
        type: 'warning',
        message: `Files older than ${fileAge} days may exceed your backup period`,
        action: 'Consider upgrading to a plan with longer backup period'
      });
    }

    return recommendations;
  }

  /**
   * Get upgrade suggestions when backup period is too short
   * @param {string} organizationId - Organization ID
   * @param {number} currentFileAge - Current age of files in days
   * @returns {Array} Array of upgrade suggestions
   */
  getUpgradeSuggestions(organizationId, currentFileAge) {
    const suggestions = [];
    
    // Find plans with longer backup periods
    Object.values(SUBSCRIPTION_PLANS).forEach(plan => {
      const backupDays = plan.limits.backupDays;
      
      if (backupDays === -1 || backupDays > currentFileAge) {
        suggestions.push({
          planId: plan.id,
          planName: plan.name,
          backupDays: backupDays === -1 ? 'Forever' : `${backupDays} days`,
          monthlyPrice: plan.monthlyPrice,
          yearlyPrice: plan.yearlyPrice,
          reason: backupDays === -1 ? 
            'Forever backup for all files' : 
            `Extend backup period to ${backupDays} days`
        });
      }
    });

    return suggestions.sort((a, b) => a.monthlyPrice - b.monthlyPrice);
  }

  // ===== SUBSCRIPTION LIMIT CHECKING METHODS =====

  /**
   * Check if organization can create more orders
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Limit check result
   */
  async checkOrderLimit(organizationId) {
    try {
      // Get organization's current plan
      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      
      const orgData = orgDoc.data();
      
      // Check for bypass limits first
      if (orgData?.bypassLimits) {
        return { 
          allowed: true, 
          current: 0, 
          limit: -1, 
          remaining: -1,
          bypassed: true
        };
      }
      
      const currentPlan = orgData?.subscriptionPlan || 'basic';
      const plan = this.getPlan(currentPlan);
      
      if (!plan) {
        throw new Error('Plan not found');
      }
      
      // Check if unlimited orders
      if (plan.limits.maxOrders === -1) {
        return { 
          allowed: true, 
          current: 0, 
          limit: -1, 
          remaining: -1 
        };
      }
      
      let currentCount;
      const ordersRef = collection(db, `organizations/${organizationId}/orders`);
      const billingWindow = getActiveBillingWindow(orgData);

      if (billingWindow && billingWindow.startDate && billingWindow.endDate) {
        // Validate dates before using in query
        const startDate = billingWindow.startDate instanceof Date && !isNaN(billingWindow.startDate.getTime())
          ? billingWindow.startDate
          : null;
        const endDate = billingWindow.endDate instanceof Date && !isNaN(billingWindow.endDate.getTime())
          ? billingWindow.endDate
          : null;

        if (startDate && endDate && endDate > startDate) {
          try {
            const ordersQuery = query(
              ordersRef,
              where('createdAt', '>=', Timestamp.fromDate(startDate)),
              where('createdAt', '<', Timestamp.fromDate(endDate))
            );
            const windowSnapshot = await getDocs(ordersQuery);
            currentCount = windowSnapshot.size;
          } catch (queryError) {
            // Falling back to full order count due to window query failure
            const fallbackSnapshot = await getDocs(ordersRef);
            currentCount = fallbackSnapshot.size;
          }
        } else {
          // Invalid window dates, fall back to full count
          const fallbackSnapshot = await getDocs(ordersRef);
          currentCount = fallbackSnapshot.size;
        }
      } else {
        // No billing window available, count all orders (backward compatibility)
        const ordersSnapshot = await getDocs(ordersRef);
        currentCount = ordersSnapshot.size;
      }

      return {
        allowed: currentCount < plan.limits.maxOrders,
        current: currentCount,
        limit: plan.limits.maxOrders,
        remaining: Math.max(0, plan.limits.maxOrders - currentCount)
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if organization can add more users
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Limit check result
   */
  async checkUserLimit(organizationId) {
    try {
      // Get organization's current plan
      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      
      const orgData = orgDoc.data();
      
      // Check for bypass limits first
      if (orgData?.bypassLimits) {
        return { 
          allowed: true, 
          current: 0, 
          limit: -1, 
          remaining: -1,
          bypassed: true
        };
      }
      
      const currentPlan = orgData?.subscriptionPlan || 'basic';
      const plan = this.getPlan(currentPlan);
      
      if (!plan) {
        throw new Error('Plan not found');
      }
      
      // Check if unlimited users
      if (plan.limits.maxUsers === -1) {
        return { 
          allowed: true, 
          current: 0, 
          limit: -1, 
          remaining: -1 
        };
      }

      // Prefer cached totalUsers count if available (updated by BusinessOwner actions)
      let currentCount = typeof orgData?.totalUsers === 'number'
        ? orgData.totalUsers
        : null;

      if (currentCount === null) {
        try {
          const usersRef = collection(db, `organizations/${organizationId}/users`);
          const usersSnapshot = await getDocs(usersRef);
          currentCount = usersSnapshot.size;
        } catch (countError) {
          if (countError?.code === 'permission-denied') {
            // Fallback to organization snapshot count (if available) or assume 0 for registration flows
            const fallbackCount = typeof orgData?.totalUsers === 'number' ? orgData.totalUsers : 0;
            return {
              allowed: fallbackCount < plan.limits.maxUsers,
              current: fallbackCount,
              limit: plan.limits.maxUsers,
              remaining: Math.max(0, plan.limits.maxUsers - fallbackCount),
              fallback: true
            };
          }
          throw countError;
        }
      }

      return {
        allowed: currentCount < plan.limits.maxUsers,
        current: currentCount,
        limit: plan.limits.maxUsers,
        remaining: Math.max(0, plan.limits.maxUsers - currentCount)
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get current usage statistics for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Usage statistics
   */
  async getUsageStats(organizationId) {
    try {
      const [orderLimit, userLimit] = await Promise.all([
        this.checkOrderLimit(organizationId),
        this.checkUserLimit(organizationId)
      ]);

      return {
        orders: orderLimit,
        users: userLimit,
        organizationId
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if organization has bypass limits enabled
   * @param {string} organizationId - Organization ID
   * @returns {Promise<boolean>} Whether bypass limits are enabled
   */
  async hasBypassLimits(organizationId) {
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        return false;
      }
      
      return orgDoc.data()?.bypassLimits === true;
    } catch (error) {
      return false;
    }
  }
}

export default new SubscriptionService();